<script lang="ts">
    // Mirrors BillDetailPanel: header, position card, summary, sponsors, action timeline,
    // committees, related bills, more-information chips.
</script>

<div
    class="overflow-hidden rounded-xl border border-gray-200 bg-white animate-pulse"
    data-testid="bill-detail-skeleton"
    aria-busy="true"
>
    <header class="border-b border-gray-200 px-6 py-5">
        <div class="mb-2 flex flex-wrap items-center gap-2">
            <div class="h-4 w-14 rounded bg-gray-300"></div>
            <div class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1">
                <div class="h-1.5 w-1.5 rounded-full bg-gray-300"></div>
                <div class="h-3 w-14 rounded bg-gray-200"></div>
            </div>
        </div>
        <div class="h-7 w-11/12 max-w-2xl rounded bg-gray-300 md:h-8"></div>
        <div class="mt-2 h-7 w-2/3 max-w-md rounded bg-gray-200 md:w-1/2"></div>
        <div class="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1">
            <div class="flex items-center gap-1.5">
                <div class="h-4 w-4 rounded bg-gray-200"></div>
                <div class="h-3.5 w-44 rounded bg-gray-200"></div>
            </div>
            <div class="flex items-center gap-1.5">
                <div class="h-4 w-4 rounded bg-gray-200"></div>
                <div class="h-3.5 w-24 rounded bg-gray-200"></div>
            </div>
        </div>
    </header>

    <div class="px-6 py-5">
        <section class="mb-8">
            <div class="mb-3 h-3 w-24 rounded bg-gray-300"></div>
            <div class="rounded-xl border border-gray-200 p-4">
                <div class="h-4 w-40 rounded bg-gray-300"></div>
                <div class="mt-4 space-y-2 border-t border-gray-100 pt-4">
                    <div class="h-3.5 w-full rounded bg-gray-200"></div>
                    <div class="h-3.5 w-5/6 rounded bg-gray-200"></div>
                    <div class="h-3.5 w-4/5 rounded bg-gray-200"></div>
                </div>
            </div>
        </section>

        <section class="mb-8">
            <div class="mb-3 h-3 w-20 rounded bg-gray-300"></div>
            <div class="space-y-2">
                <div class="h-3.5 w-full rounded bg-gray-200"></div>
                <div class="h-3.5 w-11/12 rounded bg-gray-200"></div>
                <div class="h-3.5 w-full rounded bg-gray-200"></div>
                <div class="h-3.5 w-3/4 rounded bg-gray-200"></div>
            </div>
            <div class="mt-2 h-3.5 w-20 rounded bg-gray-200"></div>
        </section>

        <section class="mb-8">
            <div class="mb-3 h-3 w-16 rounded bg-gray-300"></div>
            <div class="flex items-center gap-3">
                <div class="h-10 w-10 shrink-0 rounded-full bg-gray-200"></div>
                <div class="min-w-0 flex-1 space-y-2">
                    <div class="h-4 w-48 max-w-full rounded bg-gray-300"></div>
                    <div class="h-3 w-28 rounded bg-gray-200"></div>
                </div>
            </div>
            <div class="mt-4 border-t border-gray-100 pt-4">
                <div class="flex items-center gap-2">
                    <div class="h-3.5 w-28 rounded bg-gray-200"></div>
                    <div class="h-3 w-3 rounded bg-gray-200"></div>
                </div>
            </div>
        </section>

        <section class="mb-8">
            <div class="mb-3 h-3 w-36 rounded bg-gray-300"></div>
            <ol class="m-0 list-none p-0">
                {#each [{ title: 'w-full', date: 'w-20' }, { title: 'w-5/6', date: 'w-16' }, { title: 'w-4/5', date: 'w-20' }, { title: 'w-2/3', date: 'w-16' }, { title: 'w-3/4', date: 'w-20' }] as action, i}
                    <li class="flex gap-3">
                        <div class="flex flex-col items-center pt-1">
                            <span class="h-3 w-3 shrink-0 rounded-full ring-2 ring-white {i === 0 ? 'bg-gray-400' : 'bg-gray-200'}"></span>
                            {#if i < 4}
                                <div class="mt-1 min-h-[1.75rem] w-0.5 flex-1 {i === 4 ? '' : 'bg-gray-200'}"></div>
                            {:else}
                                <div class="mt-1 min-h-[1.25rem] w-0.5 flex-1 bg-gradient-to-b from-gray-200 to-transparent"></div>
                            {/if}
                        </div>
                        <div class="min-w-0 flex-1 pb-6">
                            <div class="h-3 rounded bg-gray-200 {action.date}"></div>
                            <div class="mt-1.5 h-4 rounded {i === 0 ? 'bg-gray-300' : 'bg-gray-200'} {action.title}"></div>
                        </div>
                    </li>
                {/each}
            </ol>
            <div class="flex items-center gap-2">
                <div class="h-3.5 w-36 rounded bg-gray-200"></div>
                <div class="h-3 w-3 rounded bg-gray-200"></div>
            </div>
        </section>

        <section class="mb-8">
            <div class="mb-3 h-3 w-40 rounded bg-gray-300"></div>
            <div class="space-y-2 pl-5">
                <div class="h-3.5 w-64 max-w-full rounded bg-gray-200"></div>
                <div class="h-3.5 w-52 max-w-full rounded bg-gray-200"></div>
            </div>
        </section>

        <section class="mb-8">
            <div class="mb-3 h-3 w-24 rounded bg-gray-300"></div>
            <ul class="m-0 list-none divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 p-0">
                {#each [{ id: 'w-12', title: 'w-3/4' }, { id: 'w-10', title: 'w-2/3' }] as related}
                    <li class="flex flex-col gap-1.5 px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
                        <div class="h-4 shrink-0 rounded bg-gray-300 {related.id}"></div>
                        <div class="h-3.5 rounded bg-gray-200 {related.title}"></div>
                    </li>
                {/each}
            </ul>
        </section>

        <section>
            <div class="mb-3 h-3 w-32 rounded bg-gray-300"></div>
            <div class="flex flex-wrap gap-2">
                <div class="h-9 w-40 rounded-lg border border-solid border-gray-200 bg-gray-100"></div>
                <div class="h-9 w-36 rounded-lg border border-solid border-gray-200 bg-gray-100"></div>
            </div>
        </section>
    </div>
</div>
